import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Users as UsersIcon, Sparkles, Send, GitBranch, Zap } from 'lucide-react';
import { api } from '@/api/client';
import { CrossSectionPanel } from './CrossSectionPanel';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { toast } from '@/state/toast';

interface AgentCitation {
  document?: string;
  document_title?: string;
  chunk_id?: string;
  text?: string;
  section_code?: string;
}

interface AgentResponse {
  agent_id: string;
  role_ru: string;
  section_code?: string;
  llm_summary_ru?: string | null;
  citations?: AgentCitation[];
  agent_role?: 'primary' | 'secondary' | 'passive';
}

interface ConsultResponse {
  mode: string;
  agents: AgentResponse[];
  query: string;
  citations_total?: number;
  classifier?: { primary: string | null; secondary: string[]; method: string };
  moderator?: {
    summary?: string;
    proposal?: string;
    consensus?: string;
  } | null;
}

interface PassiveOpinion {
  remark_id: string;
  section_code: string;
  opinion: string | null;
  cited_norms: string[];
  severity_assessment: string | null;
}

interface CollegiumTurn {
  id: string;
  role: 'user' | 'agent' | 'moderator';
  agent_id?: string;
  agent_role?: string;
  section_code?: string;
  text: string;
  citations?: AgentCitation[];
  ts: string;
}

interface Props { projectId: string; snapshotId: string | null }

function nowTs() {
  return new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function CollegiumTab({ projectId, snapshotId }: Props) {
  const [transcript, setTranscript] = useState<CollegiumTurn[]>([]);
  const [composer, setComposer] = useState('');
  const [useHierarchical, setUseHierarchical] = useState(false);
  const [passiveOpinions, setPassiveOpinions] = useState<PassiveOpinion[] | null>(null);
  const [passiveLoading, setPassiveLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript]);

  const consult = useMutation({
    mutationFn: async (question: string): Promise<ConsultResponse> => {
      return api.post(
        `/projects/${projectId}/snapshots/${snapshotId}/collegium/consult`,
        { query: question, use_llm_agents: true, use_hierarchical: useHierarchical },
      ) as Promise<ConsultResponse>;
    },
    onSuccess: (data) => {
      const ts = nowTs();
      const turns: CollegiumTurn[] = [];
      // Classifier result: prepend as system note
      if (data.classifier?.primary) {
        const secList = data.classifier.secondary?.join(', ') || '—';
        turns.push({
          id: genId(),
          role: 'moderator',
          agent_role: 'Классификатор запроса',
          text: `Ведущий раздел: **${data.classifier.primary}** · Смежные: ${secList} · Метод: ${data.classifier.method}`,
          ts,
        });
      }
      for (const a of data.agents || []) {
        const text = (a.llm_summary_ru || '').trim();
        if (a.agent_role === 'passive') continue;  // skip passive agents from transcript
        if (!text && (!a.citations || a.citations.length === 0)) continue;
        const roleLabel = a.agent_role === 'primary' ? ' [ведущий]' : a.agent_role === 'secondary' ? ' [смежный]' : '';
        turns.push({
          id: genId(),
          role: 'agent',
          agent_id: a.agent_id,
          agent_role: a.role_ru + roleLabel,
          section_code: a.section_code,
          text: text || 'Агент не сформулировал текстовый ответ, но нашёл релевантные фрагменты.',
          citations: a.citations,
          ts,
        });
      }
      if (data.moderator && (data.moderator.summary || data.moderator.proposal || data.moderator.consensus)) {
        const parts = [
          data.moderator.summary,
          data.moderator.proposal && `Предложение: ${data.moderator.proposal}`,
          data.moderator.consensus && `Консенсус: ${data.moderator.consensus}`,
        ].filter(Boolean);
        turns.push({
          id: genId(),
          role: 'moderator',
          agent_role: 'Модератор коллегии',
          text: parts.join('\n\n'),
          ts,
        });
      }
      if (turns.length === 0) {
        toast.info('Коллегия не сформировала ответ — попробуйте уточнить вопрос');
      }
      setTranscript((prev) => [...prev, ...turns]);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось получить ответ от коллегии'),
  });

  const onSend = () => {
    const text = composer.trim();
    if (!text || consult.isPending) return;
    if (!snapshotId) {
      toast.error('Сначала создайте срез проекта (snapshot)');
      return;
    }
    const userTurn: CollegiumTurn = { id: genId(), role: 'user', text, ts: nowTs() };
    setTranscript((prev) => [...prev, userTurn]);
    setComposer('');
    consult.mutate(text);
  };

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>Коллегия — мульти-агентная экспертиза</h3>
        <p className="text-muted mt-1" style={{ fontSize: 13 }}>Задайте вопрос — система соберёт ответы экспертных агентов по разделам проекта (с цитатами из документов).</p>
      </header>

      <CrossSectionPanel projectId={projectId} />

      {/* v2 mode controls */}
      <div className="flex items-center" style={{ gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setUseHierarchical(!useHierarchical)}
          className="inline-flex items-center rounded-btn"
          style={{
            padding: '6px 12px', fontSize: 12, fontWeight: 500, gap: 6,
            border: useHierarchical ? '1px solid #6366F1' : '1px solid #E5E7EB',
            background: useHierarchical ? 'rgba(99,102,241,.08)' : 'transparent',
            color: useHierarchical ? '#4338CA' : '#6B7280',
            cursor: 'pointer',
          }}
          title="Иерархический поиск v2: определяет ведущий раздел и смежные перед ответом"
        >
          <GitBranch size={13} />
          Иерархический режим v2
        </button>
        <button
          type="button"
          disabled={!snapshotId || passiveLoading}
          onClick={async () => {
            if (!snapshotId) return;
            setPassiveLoading(true);
            try {
              const res = await api.post<{ opinions: PassiveOpinion[]; stub?: boolean }>(
                `/projects/${projectId}/snapshots/${snapshotId}/collegium/passive`, {}
              );
              setPassiveOpinions(res.opinions ?? []);
            } catch (e) {
              toast.error('Пассивный режим недоступен');
            } finally {
              setPassiveLoading(false);
            }
          }}
          className="inline-flex items-center rounded-btn"
          style={{
            padding: '6px 12px', fontSize: 12, fontWeight: 500, gap: 6,
            border: '1px solid #E5E7EB', background: 'transparent',
            color: '#6B7280', cursor: !snapshotId || passiveLoading ? 'default' : 'pointer',
            opacity: !snapshotId ? 0.5 : 1,
          }}
          title="Автоматически сформировать мнения коллегии по всем открытым блокирующим замечаниям"
        >
          <Zap size={13} />
          {passiveLoading ? 'Анализ…' : 'Пассивный режим'}
        </button>
        {passiveOpinions !== null && (
          <span className="text-muted" style={{ fontSize: 11 }}>{passiveOpinions.length} мнений</span>
        )}
      </div>

      {/* Passive opinions panel */}
      {passiveOpinions !== null && passiveOpinions.length > 0 && (
        <div style={{ marginBottom: 20, padding: 16, borderRadius: 8, border: '1px solid #E5E7EB', background: '#F9FAFB' }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Пассивный режим — коллегиальные мнения</h4>
            <button type="button" onClick={() => setPassiveOpinions(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#9CA3AF' }}>Скрыть</button>
          </div>
          {passiveOpinions.map((op) => (
            <div key={op.remark_id} style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 6, background: '#fff', border: '1px solid #E5E7EB' }}>
              <div className="flex items-center" style={{ gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{op.section_code}</span>
                {op.severity_assessment && (
                  <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 4, background: op.severity_assessment === 'подтверждено' ? '#FEF2F2' : op.severity_assessment === 'снять' ? '#F0FDF4' : '#FFFBEB', color: op.severity_assessment === 'подтверждено' ? '#B91C1C' : op.severity_assessment === 'снять' ? '#15803D' : '#92400E', fontWeight: 600 }}>
                    {op.severity_assessment}
                  </span>
                )}
              </div>
              {op.opinion && <p style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>{op.opinion}</p>}
              {op.cited_norms.length > 0 && (
                <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {op.cited_norms.map((n, i) => <span key={i} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 4, background: 'rgba(99,102,241,.1)', color: '#4338CA' }}>{n}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-border rounded-card flex flex-col" style={{ overflow: 'hidden', minHeight: 'calc(100vh - 360px)' }}>
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 24, maxHeight: 'calc(100vh - 500px)', minHeight: 360 }}>
          {transcript.length === 0 ? (
            <EmptyState
              icon={UsersIcon}
              title="Коллегиум пока пуст"
              body="Задайте вопрос мульти-агентной экспертизе. Эксперты по разделам проекта (ТКР, ПОС, ООС, СМ и другим — в зависимости от загруженных документов) проанализируют вопрос и предложат решение со ссылками на источники."
            />
          ) : (
            transcript.map((t) => <TurnItem key={t.id} turn={t} />)
          )}
          {consult.isPending && (
            <div className="flex items-center text-muted" style={{ gap: 10, padding: '12px 4px', fontSize: 13 }}>
              <Sparkles size={16} stroke="#F59E0B" className="animate-pulse" />
              Коллегия обсуждает вопрос…
            </div>
          )}
        </div>

        <footer className="bg-white border-t border-border" style={{ padding: '16px 24px' }}>
          <textarea
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') onSend(); }}
            rows={2}
            placeholder="Ваш вопрос коллегии… (Ctrl+Enter — отправить)"
            className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'Inter, system-ui, sans-serif', resize: 'vertical' }}
            disabled={consult.isPending}
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-muted" style={{ fontSize: 11 }}>Ctrl+Enter — отправить</span>
            <Button variant="primary" size="sm" icon={<Send size={14} />} onClick={onSend} disabled={consult.isPending || !composer.trim()}>
              {consult.isPending ? 'Отправка…' : 'Отправить'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function TurnItem({ turn }: { turn: CollegiumTurn }) {
  if (turn.role === 'user') {
    return (
      <div className="flex mb-4 justify-end">
        <div className="rounded-card" style={{ maxWidth: '78%', padding: '10px 14px', background: '#0A1628', color: '#FFFFFF', fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
          {turn.text}
          <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>{turn.ts}</div>
        </div>
      </div>
    );
  }
  const isModerator = turn.role === 'moderator';
  const badge = turn.section_code || (isModerator ? 'МД' : '··');
  return (
    <div className="flex mb-4" style={{ gap: 12 }}>
      <div className="rounded-pill flex items-center justify-center tabular shrink-0" style={{ width: 36, height: 36, background: isModerator ? '#F59E0B' : '#0A1628', color: isModerator ? '#0A1628' : '#FFFFFF', fontSize: 11, fontWeight: 700 }}>
        {badge}
      </div>
      <div className="bg-white border border-border rounded-card" style={{ flex: 1, padding: '12px 16px', borderLeft: isModerator ? '3px solid #F59E0B' : undefined }}>
        <div className="flex items-center justify-between">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>{turn.agent_role || 'Эксперт'}</span>
          <span className="text-muted tabular" style={{ fontSize: 12 }}>{turn.ts}</span>
        </div>
        <p className="text-ink mt-1" style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{turn.text}</p>
        {turn.citations && turn.citations.length > 0 && (
          <div className="mt-3" style={{ borderTop: '1px dashed #E5E7EB', paddingTop: 8 }}>
            <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 600, marginBottom: 6 }}>Источники ({turn.citations.length})</div>
            {turn.citations.slice(0, 4).map((c, i) => (
              <div key={i} className="text-muted" style={{ fontSize: 11, lineHeight: 1.45, marginBottom: 4 }}>
                <span className="text-ink" style={{ fontWeight: 500 }}>{c.document_title || c.document || c.section_code || 'фрагмент'}</span>
                {c.text && <span> — {c.text.slice(0, 180)}{c.text.length > 180 ? '…' : ''}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
